// Page-level EVIDENCE fetching for the redesigned alarms page: the 6 h series
// behind an analog row's sparkline and mini-bar, and the 24 h past-trip ticks
// behind a binary row's strip.
//
// No alarms surface has ever read `/telemetry/range` (data-mapping §5) — this
// is a new fetch, and it follows the same convention the value lines already
// do: the PAGE fetches once and hands maps down, so two screens can never
// disagree about what a signal did, and a row view stays sample-free and
// renders identically offline.
//
// Idiom follows `useSignalCatalog`: `api()` helper, per-boat state, never
// module-cached (one boat's series must not leak into another's page).
//
// The honesty contract is that a failure degrades to an EMPTY map, never to a
// fabricated line: the screens render their "no recent data" placeholder, and
// the value line beside it keeps carrying the real freshness wording. Nothing
// here ever invents a point.

import { useEffect, useMemo, useState } from 'react';

import { api } from '../api';
import type { SeriesPoint } from './alarmRedesign';
import type { RangeSeries } from './useTelemetry';

/** The contract's per-call path cap (`api_contracts.md` §5.1: `paths` ≤ 20). */
export const RANGE_MAX_PATHS = 20;

/** The analog evidence window — one call, 5-minute buckets, plenty for 100px. */
export const EVIDENCE_RANGE_MS = 6 * 60 * 60 * 1000;

/** Buckets requested across that window (≈5 min each). */
export const EVIDENCE_RANGE_POINTS = 72;

/** The binary evidence window — "past trips" on the 24 h tick strip. */
export const EVIDENCE_HISTORY_MS = 24 * 60 * 60 * 1000;

/** The history read's clamp (`limit` is clamped to [1, 200] server-side). */
export const EVIDENCE_HISTORY_LIMIT = 200;

/** One analog signal's recent past, ready for {@link sparkGeometry}. */
export interface EvidenceSeries {
  /** The STORE unit the series came back in — the trigger's own domain. */
  readonly unit: string | null;
  /** Bucket averages, oldest first. Empty when the window held no samples. */
  readonly points: SeriesPoint[];
  /** Observed floor/ceiling across the window (bucket min/max), or `null`. */
  readonly min: number | null;
  readonly max: number | null;
}

/** What the page hands to the screens. */
export interface AlarmEvidence {
  /** Keyed by resolved telemetry path. Absent ⇒ nothing to draw, say so. */
  readonly series: ReadonlyMap<string, EvidenceSeries>;
  /** Keyed by alert id: epoch-ms of each past firing in the last 24 h. */
  readonly ticks: ReadonlyMap<string, number[]>;
  /** True while the first load for the current inputs is in flight. */
  readonly loading: boolean;
}

const EMPTY: AlarmEvidence = { series: new Map(), ticks: new Map(), loading: false };

/**
 * Split a path list into contract-sized calls. Deduped and sorted first, so the
 * same set produces the same chunks — and the same effect key — however the
 * caller ordered it (the pattern `latestPathsQuery` established).
 */
export function chunkPaths(paths: readonly string[], size: number = RANGE_MAX_PATHS): string[][] {
  const unique = [...new Set(paths.filter((p) => p.trim().length > 0))].sort();
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += size) chunks.push(unique.slice(i, i + size));
  return chunks;
}

/**
 * Reduce one wire series to plot points plus its observed bounds.
 *
 * The bounds come from the buckets' own `min`/`max`, not from the averages: a
 * mini-bar scaled to averages would understate how far a value actually swung,
 * and the bar exists precisely to say where in its range a reading sits.
 */
export function toEvidenceSeries(series: RangeSeries | undefined): EvidenceSeries {
  const points: SeriesPoint[] = [];
  let min: number | null = null;
  let max: number | null = null;
  for (const p of series?.points ?? []) {
    const t = Date.parse(p.t);
    if (!Number.isFinite(t) || !Number.isFinite(p.avg)) continue;
    points.push({ t, v: p.avg });
    const lo = Number.isFinite(p.min) ? p.min : p.avg;
    const hi = Number.isFinite(p.max) ? p.max : p.avg;
    min = min === null ? lo : Math.min(min, lo);
    max = max === null ? hi : Math.max(max, hi);
  }
  points.sort((a, b) => a.t - b.t);
  return { unit: series?.unit ?? null, points, min, max };
}

/** One history entry, as much of it as the tick strip reads. */
interface HistoryEntryLike {
  readonly time: string;
  readonly transition: string;
}

/**
 * The epoch-ms of every past FIRING in a history page.
 *
 * Only `Active` transitions count. These are alarm trips, not raw sensor
 * closures — a pump that cycled without tripping its alarm shows nothing, which
 * is exactly what "past trips" should mean.
 */
export function activeTicks(entries: readonly HistoryEntryLike[] | undefined): number[] {
  const ticks: number[] = [];
  for (const entry of entries ?? []) {
    if (entry.transition !== 'Active') continue;
    const ms = Date.parse(entry.time);
    if (Number.isFinite(ms)) ticks.push(ms);
  }
  return ticks.sort((a, b) => a - b);
}

interface RangeResponse {
  series?: Record<string, RangeSeries | undefined>;
}

interface HistoryResponse {
  entries?: HistoryEntryLike[];
}

export interface UseAlarmEvidenceInput {
  readonly boatId: string;
  /** Resolved paths of the analog alarms worth a sparkline. */
  readonly analogPaths: readonly string[];
  /** Alert ids of the CURRENTLY SURFACED binary alarms. */
  readonly binaryAlertIds: readonly string[];
  /**
   * Changes whenever the alerts snapshot did — the page passes something that
   * moves on refresh (`mirroredAt`, or a counter). Evidence is only ever read
   * next to a snapshot, so it refreshes with it rather than on its own timer.
   */
  readonly refreshKey?: string | number;
}

/**
 * Fetch the evidence for one page's worth of alarms.
 *
 * Both reads are best-effort. A 404, a boat with no history, an expired
 * session mid-poll — all land as an empty map and a console line, because the
 * page's job is still to render the honest reduced cell, not an error state
 * over the whole screen.
 */
export function useAlarmEvidence(input: UseAlarmEvidenceInput): AlarmEvidence {
  const { boatId, refreshKey } = input;
  const [state, setState] = useState<AlarmEvidence>(EMPTY);

  // Stable effect keys: the hook must not refetch because a parent rebuilt an
  // array with the same contents in a different order.
  const pathsKey = useMemo(
    () => chunkPaths(input.analogPaths).flat().join(','),
    [input.analogPaths]
  );
  const idsKey = useMemo(
    () => [...new Set(input.binaryAlertIds.filter((id) => id.length > 0))].sort().join(','),
    [input.binaryAlertIds]
  );

  useEffect(() => {
    const paths = pathsKey === '' ? [] : pathsKey.split(',');
    const ids = idsKey === '' ? [] : idsKey.split(',');
    if (boatId === '' || (paths.length === 0 && ids.length === 0)) {
      setState(EMPTY);
      return;
    }
    let alive = true;
    setState((prev) => ({ ...prev, loading: true }));

    void (async () => {
      const now = Date.now();
      const series = new Map<string, EvidenceSeries>();
      const ticks = new Map<string, number[]>();

      const rangeCalls = chunkPaths(paths).map(async (chunk) => {
        const from = new Date(now - EVIDENCE_RANGE_MS).toISOString();
        try {
          const res = await api<RangeResponse>(
            `/v1/boats/${boatId}/telemetry/range?paths=${encodeURIComponent(chunk.join(','))}` +
              `&from=${encodeURIComponent(from)}&points=${EVIDENCE_RANGE_POINTS}`
          );
          for (const path of chunk) {
            const wire = res.series?.[path];
            if (wire === undefined) continue;
            series.set(path, toEvidenceSeries(wire));
          }
        } catch (err) {
          // Degrade, never block: the rows show "no recent data" and the value
          // line beside them still carries the real freshness wording.
          console.warn('alarm evidence: telemetry range failed', err);
        }
      });

      const historyCalls = ids.map(async (alertId) => {
        const from = new Date(now - EVIDENCE_HISTORY_MS).toISOString();
        try {
          const res = await api<HistoryResponse>(
            `/v1/boats/${boatId}/alerts/history?alertId=${encodeURIComponent(alertId)}` +
              `&from=${encodeURIComponent(from)}&limit=${EVIDENCE_HISTORY_LIMIT}`
          );
          ticks.set(alertId, activeTicks(res.entries));
        } catch (err) {
          console.warn('alarm evidence: alert history failed', err);
        }
      });

      await Promise.all([...rangeCalls, ...historyCalls]);
      if (!alive) return;
      setState({ series, ticks, loading: false });
    })();

    return () => {
      alive = false;
    };
  }, [boatId, pathsKey, idsKey, refreshKey]);

  return state;
}
